import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ItemPLConfirmationModal } from '@/types/modal';
import { useConfirmLitigation, useItem } from '@/hooks/item';
import { ErrorModal, StandardModal, NoFooterModal, SuccessModal } from '@/components/molecules/common/Modal';
import { DateTimePicker, Radio } from '@/components/molecules/common/Form';
import styles from './Modal.module.scss';
import dayjs from 'dayjs';
import { Skeleton, Stack } from '@mui/material';
import { transformDateFrance } from '@/utilities/datetime';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '@/types/query';

type RadioType = 'now' | 'past';
const MODAL_WIDTH = 550;

const View: React.FC<{ modal: ItemPLConfirmationModal }> = ({ modal }) => {
  const { t } = useTranslation();
  const { item, onClose } = modal;

  const currentTime = dayjs(new Date());
  const franceTime = transformDateFrance(currentTime.toString());

  const [dueDate, setDueDate] = useState<dayjs.Dayjs | null>(franceTime);

  const [type, setType] = useState<RadioType>('now');

  const itemR = useItem(item.productId, false);

  const submitR = useConfirmLitigation();

  const queryClient = useQueryClient();

  const title = t('item.postal_litigation.plConfirmation.confirmationTitle');

  const maxTime = dueDate?.isSame(currentTime, 'day') ? franceTime : franceTime?.endOf('day');

  const isSubmitButtonEnabled = type !== 'past' || (dueDate && dueDate.isValid() && dueDate.isBefore(franceTime));

  useEffect(() => {
    if (submitR.kind === 'Completed') {
      itemR.refetch();
      queryClient.invalidateQueries({ queryKey: [QueryKeys.POSTAL_LITIGATION] });
    }
  }, [submitR.kind]);

  if (submitR.kind === 'Loading') {
    return (
      <NoFooterModal
        title={title}
        body={
          <>
            <Skeleton
              width={MODAL_WIDTH}
              height={80}
              variant="rounded"
            />
          </>
        }
        onClose={onClose}
        modalName="pl_confirmation"
      />
    );
  }

  if (submitR.kind === 'Error') {
    return (
      <ErrorModal
        title={title}
        error={submitR.error}
        onClose={onClose}
        useOriginMsg={true}
      />
    );
  }

  if (submitR.kind === 'Completed') {
    return (
      <SuccessModal
        title={title}
        onClose={onClose}
      />
    );
  }

  const body = (
    <div className={styles.containerGap}>
      <div className={styles.text1rem}>{`${t('common.item')} #${item.productId}`}</div>
      <div className={styles.text1rem}>{t('item.postal_litigation.plConfirmation.confirmationDes')}</div>

      <Stack>
        <Radio
          label={t('item.postal_litigation.plConfirmation.now')}
          onChange={() => setType('now')}
          checked={type === 'now'}
        />
        <Radio
          label={t('item.postal_litigation.plConfirmation.past')}
          onChange={() => setType('past')}
          checked={type === 'past'}
        />
      </Stack>

      <DateTimePicker
        disabled={type !== 'past'}
        label={t('item.postal_litigation.plConfirmation.date')}
        value={dueDate}
        ampm={false}
        maxDate={franceTime || undefined}
        maxTime={maxTime || undefined}
        onChange={(newValue: dayjs.Dayjs | null) => setDueDate(newValue)}
      />
    </div>
  );

  const onSubmit = () => {
    if (!dueDate || !dueDate.isValid() || !item) return;

    submitR.mutate({
      productId: item.productId.toString(),
      moment: type,
      time: dueDate ? dueDate.toISOString() : '',
    });
  };

  return (
    <StandardModal
      title={title}
      onClose={onClose}
      onSubmit={onSubmit}
      submittable={dueDate != null && item.orderId != '' && !!isSubmitButtonEnabled}
      body={body}
      containerStyle={{ maxWidth: MODAL_WIDTH }}
      modalName="pl_confirmation"
    />
  );
};

export default View;
